import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Alert,
  Paper,
  CircularProgress,
  Badge,
  Chip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  ExitToApp as ExitToAppIcon,
  Notifications as NotificationsIcon,
  QueryStats as QueryStatsIcon,
  Warning as WarningIcon,
  Feedback as FeedbackIcon,
  ViewQuilt as ViewQuiltIcon,
  DataObject as DataObjectIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import AdminService from '../../services/AdminService';

const drawerWidth = 280; // Increased width to accommodate metrics

const NavItem = ({ path, icon, label, onClick, isActive, open, badge }) => (
  <ListItem disablePadding>
    <ListItemButton
      selected={isActive}
      onClick={onClick}
      sx={{
        borderRadius: '0 24px 24px 0',
        mr: 1,
        '&.Mui-selected': {
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          '& .MuiListItemIcon-root': {
            color: 'white',
          },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        {badge ? (
          <Badge badgeContent={badge} color="error">
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </ListItemIcon>
      <ListItemText primary={label} sx={{ opacity: open ? 1 : 0 }} />
    </ListItemButton>
  </ListItem>
);

const AdminSidebar = ({ activePath, onNavigate }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [expanded, setExpanded] = useState(true);
  
  // Status states
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const userData = await AdminService.getCurrentUser();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error fetching current user:', error);
        // Check if error is due to authentication (401)
        if (error.response && error.response.status === 401) {
          // Clear invalid token and redirect to login
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
        }
      }
    };

    fetchCurrentUser();
  }, [navigate]);
  
  // Fetch real-time metrics on component mount and periodically
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const data = await AdminService.getRealtimeSummary();
        setMetrics(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching real-time metrics:', err);
        // Check if error is due to authentication (401)
        if (err.response && err.response.status === 401) {
          // Clear invalid token and redirect to login
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
        } else {
          setError('Failed to load metrics');
        }
      } finally {
        setLoading(false);
      }
    };
    
    // Initial fetch
    fetchMetrics();
    
    // Set up interval for periodic refresh
    const intervalId = setInterval(fetchMetrics, refreshInterval);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [refreshInterval, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleMenuItemClick = (path) => {
    // Use the onNavigate prop to update the active path in the parent component
    if (onNavigate) {
      onNavigate(path);
    }
    
    // Also navigate to maintain URL state, but don't actually load the page
    navigate(path, { replace: true });
  };

  const menuItems = [
    // {
    //   text: 'Dashboard',
    //   icon: <DashboardIcon />,
    //   path: '/admin/dashboard',
    //   roles: ['admin', 'auditor', 'viewer']
    // },
    {
      text: 'Audit Logs',
      icon: <HistoryIcon />,
      path: '/admin/audit-logs',
      roles: ['admin', 'auditor'],
      badge: metrics?.recent_activity_count > 0 ? metrics.recent_activity_count : null
    },
    {
      text: 'User Management',
      icon: <PeopleIcon />,
      path: '/admin/users',
      roles: ['admin']
    },
    {
      text: 'Feedback Analytics',
      icon: <FeedbackIcon />,
      path: '/admin/feedback-analytics',
      roles: ['admin', 'auditor']
    },
    // {
    //   text: 'System Metrics',
    //   icon: <AnalyticsIcon />,
    //   path: '/admin/metrics',
    //   roles: ['admin', 'auditor']
    // },
    {
      text: 'Query Logs',
      icon: <QueryStatsIcon />,
      path: '/admin/query-logs',
      roles: ['admin', 'auditor']
    },
    // {
    //   text: 'Alerts',
    //   icon: <NotificationsIcon />,
    //   path: '/admin/alerts',
    //   roles: ['admin', 'auditor'],
    //   badge: metrics?.active_alerts > 0 ? metrics.active_alerts : null
    // },
    {
      text: 'Data Sources',
      icon: <StorageIcon />,
      path: '/admin/data-sources',
      roles: ['admin']
    },
    {
      text: 'Chunk Settings',
      icon: <ViewQuiltIcon />,
      path: '/admin/chunk-settings',
      roles: ['admin']
    },
    {
      text: 'Embeddings Manager',
      icon: <DataObjectIcon />,
      path: '/admin/embeddings',
      roles: ['admin']
    },
    {
      text: 'Model Configuration',
      icon: <SettingsIcon />,
      path: '/admin/model-configuration',
      roles: ['admin']
    },
    {
      text: 'Rate Limiting',
      icon: <SpeedIcon />,
      path: '/admin/rate-limiting',
      roles: ['admin']
    },
    // {
    //   text: 'Settings',
    //   icon: <SettingsIcon />,
    //   path: '/admin/settings',
    //   roles: ['admin']
    // }
  ];

  const hasAccess = (item) => {
    if (!currentUser || !currentUser.roles) return false;
    return item.roles.some(role => currentUser.roles.includes(role));
  };

  const isActive = (path) => {
    return activePath === path;
  };
  
  // Get status color based on system health
  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'healthy':
        return 'success';
      default:
        return 'info';
    }
  };

  return (
    <Drawer
      variant="permanent"
      open={expanded}
      sx={{
        width: expanded ? drawerWidth : 64,
        flexShrink: 0,
        transition: 'width 0.2s ease-in-out',
        '& .MuiDrawer-paper': {
          position: 'relative',
          whiteSpace: 'nowrap',
          width: expanded ? drawerWidth : 64,
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRight: `1px solid ${theme.palette.divider}`
        },
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-end' : 'center',
          px: [1],
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        {expanded ? (
          <>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 1, color: theme.palette.primary.main }}>
              Admin Panel
            </Typography>
            <IconButton onClick={handleToggleExpanded}>
              <ChevronLeftIcon />
            </IconButton>
          </>
        ) : (
          <IconButton onClick={handleToggleExpanded}>
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
      
      {/* System Status Section (Only visible when expanded) */}
      {expanded && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            System Status
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ) : metrics ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip 
                  icon={metrics.system_status !== 'healthy' ? <WarningIcon /> : null}
                  label={metrics.system_status.toUpperCase()} 
                  color={getStatusColor(metrics.system_status)}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  as of {new Date(metrics.timestamp).toLocaleTimeString()}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">CPU Usage</Typography>
                  <Typography variant="caption" fontWeight="bold">{metrics.cpu_usage}%</Typography>
                </Box>
                <Box 
                  sx={{ 
                    height: 4, 
                    width: '100%', 
                    backgroundColor: 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box 
                    sx={{ 
                      height: '100%', 
                      width: `${metrics.cpu_usage}%`, 
                      backgroundColor: metrics.cpu_usage > 90 
                        ? 'error.main' 
                        : metrics.cpu_usage > 70 
                          ? 'warning.main' 
                          : 'success.main',
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Memory Usage</Typography>
                  <Typography variant="caption" fontWeight="bold">{metrics.memory_usage}%</Typography>
                </Box>
                <Box 
                  sx={{ 
                    height: 4, 
                    width: '100%', 
                    backgroundColor: 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box 
                    sx={{ 
                      height: '100%', 
                      width: `${metrics.memory_usage}%`, 
                      backgroundColor: metrics.memory_usage > 90 
                        ? 'error.main' 
                        : metrics.memory_usage > 70 
                          ? 'warning.main' 
                          : 'success.main',
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Disk Usage</Typography>
                  <Typography variant="caption" fontWeight="bold">{metrics.disk_usage}%</Typography>
                </Box>
                <Box 
                  sx={{ 
                    height: 4, 
                    width: '100%', 
                    backgroundColor: 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box 
                    sx={{ 
                      height: '100%', 
                      width: `${metrics.disk_usage}%`, 
                      backgroundColor: metrics.disk_usage > 90 
                        ? 'error.main' 
                        : metrics.disk_usage > 70 
                          ? 'warning.main' 
                          : 'success.main',
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Tooltip title="Total indexed documents">
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{metrics.total_documents}</Typography>
                    <Typography variant="caption" color="text.secondary">Documents</Typography>
                  </Box>
                </Tooltip>
                
                <Tooltip title="Queries per hour">
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{metrics.query_rate}</Typography>
                    <Typography variant="caption" color="text.secondary">Queries/hr</Typography>
                  </Box>
                </Tooltip>
                
                <Tooltip title="Recent activities in last 15 minutes">
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{metrics.recent_activity_count}</Typography>
                    <Typography variant="caption" color="text.secondary">Activities</Typography>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
      
      <List component="nav" sx={{ mt: 1 }}>
        {/* Main Navigation */}
        {expanded ? (
          menuItems.map((item) => (
            hasAccess(item) && (
              <NavItem
                key={item.text}
                path={item.path}
                icon={item.icon}
                label={item.text}
                onClick={() => handleMenuItemClick(item.path)}
                isActive={isActive(item.path)}
                open={expanded}
                badge={item.badge}
              />
            )
          ))
        ) : (
          // Collapsed sidebar with just icons
          menuItems.map((item) => (
            hasAccess(item) && (
              <Tooltip key={item.text} title={item.text} placement="right">
                <ListItemButton
                  selected={isActive(item.path)}
                  onClick={() => handleMenuItemClick(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: 'center',
                    px: 2.5,
                    borderRadius: '0 24px 24px 0',
                    mr: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.badge ? (
                      <Badge badgeContent={item.badge} color="error">
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )
          ))
        )}
        
        <Divider sx={{ my: 1 }} />
        
        {/* User section */}
        {expanded && currentUser && (
          <Box sx={{ p: 2, mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Logged in as
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
              <strong>{currentUser.username}</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {currentUser.roles?.map(role => (
                <Chip 
                  key={role}
                  label={role}
                  size="small"
                  color={role === 'admin' ? 'primary' : 'default'}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            component={Link}
            to="/"
            sx={{
              minHeight: 48,
              justifyContent: expanded ? 'initial' : 'center',
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: expanded ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Back to App" 
              sx={{ opacity: expanded ? 1 : 0 }} 
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

export default AdminSidebar;